/*
 * Copyright (c) 2019 Honeywell International, Inc. All rights reserved.
 *
 * This file contains trade secrets of Honeywell International, Inc.  No part
 *  may be reproduced or transmitted in any form by any means or for any
 *  purpose without the express written permission of Honeywell.
 *
 */

import axiosInstance from "./ForgeAxiosInstance";
import DIYAppConstants from "../../utils/DIYAppConstants";
import {
  IOrganizationDetails,
  ISiteDetails
} from "./IGatewayRegistrationInterfaces";
import {AxiosResponse} from "axios";
import {action, computed, observable} from "mobx";
import GlobalStore from "../GlobalStore/GlobalStore";
import featureFlagStore from '../../utils/FeatureFlags/APFeatureFlagsStore';


/**
 * This store retrives the list of sites that the user has access to
 */
export default class SiteSelectionStore extends GlobalStore {

  @observable
  siteFilter: string;

  @observable
  siteList: Array<ISiteDetails>;

  /**
   * Initialize the customer List
   */
  constructor() {
    super();
    this.siteList = [];
    this.siteFilter = '';
  }

  /**
   * Set the site filter value
   * @param siteFilter
   */
  @action
  setSiteFilter(siteFilter: string = '') {
    this.siteFilter = siteFilter;
  }

  /**
   * Get the list of sites filtered by site filter
   * For now check in the site name and address
   */
  @computed
  get filteredSiteList() {
    let siteFilter = this.siteFilter;
    if (!!siteFilter) {
      return this.siteList.filter( (siteDetails: ISiteDetails) => {
        return !!siteDetails.siteName && siteDetails.siteName.toUpperCase().indexOf(siteFilter.toUpperCase()) !== -1 ||
          !!siteDetails.address1 && siteDetails.address1.toUpperCase().indexOf(siteFilter.toUpperCase()) !== -1 ||
          !!siteDetails.address2 && siteDetails.address2.toUpperCase().indexOf(siteFilter.toUpperCase()) !== -1;
      });
    }
    return this.siteList;
  }

  /**
   * Get the list of sites for the user.
   */
  @action
  getSiteList = (): Promise<Array<ISiteDetails>> => {
    if(featureFlagStore.getForgeDiyAppEnabled){
      return this.getSitesForUserAuthGroup().then((responseList: ISiteDetails[]) => {
        return this.siteList = ([] as ISiteDetails[]).concat(...responseList);
      });
    }else{
      return this.getSiteListPromise().then((responseList: ISiteDetails[][]) => {
        return this.siteList = ([] as ISiteDetails[]).concat(...responseList);
      });
    }
  };

  /**
   * Get the promise that corresponds to the full list of sites the user has access to across all the customers
   */
  getOrganizationList = (): Promise<IOrganizationDetails[]> => {
    let envVariables = this.envVariables;
    let organizationListURL =
      `${envVariables.PORTAL_API_GATEWAY_URL}${DIYAppConstants.API_CUSTOMER_LIST}`;
    return axiosInstance.getInstance().get(organizationListURL).then((res: AxiosResponse) => {
      let organizations = res.data;
      let organizationList: IOrganizationDetails[] = [];
      organizations.forEach((organization: any) => {
        organizationList.push({
          organizationId: organization['customerId'],
          organizationName: organization['customerName'],
          address1: organization['addressLine1'],
          address2: organization['addressLine2'],
        });
      });
      return organizationList;
    });
  };

  /**
   * Get the promise corresponding to the list of sites for a particular customer/organization
   * @param organization
   */
  getCustomerSiteList = (organization: IOrganizationDetails): Promise<ISiteDetails[]> => {
    let envVariables = this.envVariables;
    let siteListURL =
      `${envVariables.PORTAL_API_GATEWAY_URL}${DIYAppConstants.API_SITE_LIST(organization.organizationId)}`;
    let siteList: Array<ISiteDetails> = [];
    return axiosInstance.getInstance().get(siteListURL).then((res: AxiosResponse) => {
      let siteListObject = res.data;
      if (siteListObject.StatusCode === 200) {
        let sites = siteListObject.Result;
        sites.forEach((site: any) => {
          siteList.push({
            siteId: site['OrganizationId'],
            siteName: site['OrganizationName'],
            address1: site['AddressLine1'] || organization.address1 || DIYAppConstants.SITE_ADDRESS_PLACEHOLDER,
            address2: site['AddressLine2'] || organization.address2 || DIYAppConstants.SITE_ADDRESS_PLACEHOLDER,
            customerDetails: organization
          });
        });
        return siteList;
      }
      throw new Error("Error fetching site list");
    });
  };

  /**
   * Returns all the sites the logged in user has access to
   */
  getAllSitesForUser = () => {
    let envVariables = this.envVariables;
    let allUserSiteListURL =
      `${envVariables.CLOUDCONNECTOR_API_BASE_URL}${DIYAppConstants.API_ALL_SITE_LIST}`;
    let siteList: Array<ISiteDetails> = [];
    let instance = axiosInstance.getCloudConnectorInstance();
    return instance.get(allUserSiteListURL).then((res: AxiosResponse) => {
      let siteListObject = res.data;
      let sites = siteListObject;
      sites.forEach((site: any) => {
        siteList.push({
          siteId: site['organizationId'],
          siteName: site['organizationName'],
          customerDetails: {
            organizationId: '',
            organizationName:''
          }
        });
      })
      return [siteList];
    }).catch(() => {
      return [];
    });
  };

  /**
   * Get the promise corresponding to the list of sites for a particular customer/organization
   * @param organization
   */
  getSitesForUserAuthGroup = () => {
    let envVariables = this.envVariables;
    let siteListURL =
      `${envVariables.PORTAL_API_GATEWAY_URL}${DIYAppConstants.API_SITE_FOR_USER_AUTH_GROUP}`;
    let siteList: Array<ISiteDetails> = [];
    return axiosInstance.getInstance().get(siteListURL).then((res: AxiosResponse) => {
      let siteListObject = res.data;
      if (siteListObject && siteListObject.StatusCode === 200) {
        let sites = siteListObject.Result;
        sites.forEach((site: any) => {
          siteList.push({
            siteId: site['OrganizationId'],
            siteName: site['OrganizationName'],
            customerDetails: {
              organizationId: '',
              organizationName:''
            }
          });
        });
      }
      return siteList;
    }).catch(()=>{
      return siteList;
    });
  };

  /**
   * Get the promise corresponding to list of all the sites for a user
   */
  private getSiteListPromise = (): Promise<ISiteDetails[][]> => {
    let limit = (this.envVariables.MAX_CUSTOMER_DETAILS_SUPPORTED) ? parseInt(this.envVariables.MAX_CUSTOMER_DETAILS_SUPPORTED) : 10;
    return this.getOrganizationList().then((organizationList: IOrganizationDetails[]) => {
      // Make a choice here.
      // If the number of customers involved is very high, getting all the list and ten querying the sites for each of
      // the customers will lead to computational crash and hence if is better to go for a rather slow but sure API for the same.
      if (organizationList.length < limit) {
        let promiseList: Promise<ISiteDetails[]>[] = [];
        organizationList.forEach((organization: IOrganizationDetails) => {
          let orgSiteListPromise = this.getCustomerSiteList(organization);
          promiseList.push(orgSiteListPromise);
        });
        return Promise.all(promiseList);
      }
      else {
        return this.getAllSitesForUser();
      }
    });
  }

  

}
